/**
 * Dashboard shell: top bar + left nav. Wraps every /dashboard/* route.
 */
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { Badge } from './ui';

const NAV = [
  { to: '/dashboard', label: 'Overview', icon: '◧', end: true },
  { to: '/dashboard/tokens', label: 'Access Tokens', icon: '🔑' },
  { to: '/dashboard/publish', label: 'My Packages', icon: '📦' },
  { to: '/dashboard/devices', label: 'Devices', icon: '💻' },
  { to: '/dashboard/settings', label: 'Settings', icon: '⚙️' },
];

export function DashboardLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <NavLink to="/" className="flex items-center gap-2 font-bold text-slate-900">
            <Logo /> <span>SwarmAI Hub</span>
          </NavLink>
          <div className="flex items-center gap-3 text-sm">
            {user && (
              <span className="hidden text-slate-500 sm:inline">
                {user.email} {user.role === 'admin' && <Badge color="purple">admin</Badge>}
              </span>
            )}
            <button
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
              className="rounded-lg px-3 py-1.5 font-medium text-slate-600 hover:bg-slate-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-8 px-6 py-8">
        <nav className="hidden w-56 shrink-0 md:block">
          <ul className="space-y-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                      isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-100'
                    }`
                  }
                >
                  <span aria-hidden>{item.icon}</span> {item.label}
                </NavLink>
              </li>
            ))}
            {user?.role === 'admin' && (
              <li>
                <NavLink
                  to="/dashboard/admin"
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                      isActive ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                    }`
                  }
                >
                  <span aria-hidden>🛡️</span> Admin
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

export function Logo() {
  return (
    <svg viewBox="0 0 32 32" className="h-7 w-7">
      <rect width="32" height="32" rx="7" fill="#1f47f5" />
      <circle cx="16" cy="11" r="3.2" fill="#fff" />
      <circle cx="9.5" cy="21" r="3.2" fill="#fff" />
      <circle cx="22.5" cy="21" r="3.2" fill="#fff" />
      <path d="M16 11 L9.5 21 M16 11 L22.5 21 M9.5 21 L22.5 21" stroke="#fff" strokeWidth="1.6" fill="none" opacity="0.7" />
    </svg>
  );
}
